import React, { useCallback, useEffect, useState } from "react";
import { AdminTable } from "../Table/AntTable";
import type { DataTableColumn, ActionConfig } from "../Table/AntTable";
import { message, Tag } from "antd";
import {
  CustomChallenge,
  getActiveCustomChallenges,
} from "../weeklyChallenges/customChallenge";
import EditChallengeModal from "../ui/EditChallengeModal";
import { AdminDeleteModal } from "../AdminDeleteModal";
import { deleteChallenge } from "../../services/firebase";

const ViewChallenges: React.FC = () => {
  const [data, setData] = useState<CustomChallenge[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingChallengeId, setEditingChallengeId] = useState<string | null>(
    null
  );
  const [deletingChallengeId, setDeletingChallengeId] = useState<string | null>(
    null
  );

  // Define columns with custom rendering
  const columns: DataTableColumn<CustomChallenge>[] = [
    {
      title: "Thumbnail",
      dataIndex: "image",
      sorter: true,
      width: 100,
      render: (value: string) => {
        if (!value) return null;
        return (
          <img
            src={value}
            alt="Thumbnail"
            style={{
              width: "80px",
              height: "80px",
              objectFit: "cover",
              borderRadius: "4px",
            }}
          />
        );
      },
    },
    {
      title: "Name",
      dataIndex: "title",
      sorter: true,
      render: (value: string) => <strong>{value}</strong>,
    },
    {
      title: "Author",
      dataIndex: "author",
      sorter: true,
      render: (value: string) => <strong>{value}</strong>,
    },
    {
      title: "Year",
      dataIndex: "year",
      align: "center",
      sorter: (a, b) => Number(a.year) - Number(b.year),
    },
    {
      title: "Week",
      dataIndex: "week",
      align: "center",
      sorter: (a, b) => Number(a.week) - Number(b.week),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      width: 120,
      sorter: (a, b) => {
        const getTime = (value: any) => {
          if (!value) return 0;
          if (value.toDate) return value.toDate().getTime();
          if (value.seconds) return value.seconds * 1000;
          if (value instanceof Date) return value.getTime();
          return 0;
        };
        return getTime(a.createdAt) - getTime(b.createdAt);
      },
      render: (value: any) => {
        if (!value) return "-";
        try {
          let date: Date;
          // Handle Firebase Timestamp
          if (value.toDate) {
            date = value.toDate();
          } else if (value.seconds) {
            date = new Date(value.seconds * 1000);
          } else if (value instanceof Date) {
            date = value;
          } else {
            date = new Date(value);
          }
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        } catch (error) {
          return "-";
        }
      },
    },
  ];

  const fetchChallenges = async () => {
    setLoading(true);
    try {
      const fetched = await getActiveCustomChallenges();
      setData(fetched);
      //setAllChallenges(fetched.sort((a, b) => b.year.localeCompare(a.year)));
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch challenges");
    } finally {
      setLoading(false);
      console.log("Fetched challenges");
    }
  };

  useEffect(() => {
    fetchChallenges();
  }, []);

  // Define action handlers
  const handleEdit = (record: CustomChallenge) => {
    message.info(`Editing challenge: ${record.title}`);
    if (record.id) {
      setEditingChallengeId(record.id);
    }
  };

  const handleDelete = (record: CustomChallenge) => {
    if (record.id) {
      setDeletingChallengeId(record.id);
    }
  };

  const onDeleteChallenge = async (challengeId: string) => {
    try {
      await deleteChallenge(challengeId);
      message.success("Challenge deleted successfully");
      setDeletingChallengeId(null);
      fetchChallenges();
    } catch (error) {
      console.error("Failed to delete blog:", error);
    }
  };

  const actions: ActionConfig<CustomChallenge> = {
    onEdit: handleEdit,
    onDelete: handleDelete,
    editText: "Edit",
    deleteText: "Delete",
  };

  return (
    <div style={{ padding: "24px" }}>
      <AdminTable<CustomChallenge>
        columns={columns}
        data={data}
        rowKey="id"
        loading={loading}
        actions={actions}
        pageSize={10}
        searchable={true}
        searchPlaceholder="Search users by any field..."
        bordered={true}
        size="middle"
        showSizeChanger={true}
        pageSizeOptions={[10, 20, 50, 100]}
      />
      {editingChallengeId && (
        <EditChallengeModal
          challengeId={editingChallengeId}
          isAdmin={true}
          onSaveSuccess={() => {
            message.success("Challenge updated successfully");
            setEditingChallengeId(null);
            fetchChallenges();
          }}
          isEdit={true}
          onClose={() => setEditingChallengeId(null)}
        />
      )}
      {deletingChallengeId && (
        <AdminDeleteModal
          onConfirm={() => onDeleteChallenge(deletingChallengeId)}
          description="Are you sure you want to delete this challenge? This action cannot be undone."
          isDeleting={true}
          onClose={() => setDeletingChallengeId(null)}
        />
      )}
    </div>
  );
};

export default ViewChallenges;
